import { ApiError } from "@/common/lib/api";
import { useCurrentAccount } from "@/features/accounts/hooks/useCurrentAccount";
import { AlertTriangle, ArrowLeft, Download, Info } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { downloadImportTemplate, useImportConfirm, useImportPreview } from "../api/importApi";
import { ImportConfirmResult } from "../components/ImportConfirmResult";
import { ImportErrorList } from "../components/ImportErrorList";
import { ImportGuideSection } from "../components/ImportGuideSection";
import { ImportMobileBlocker } from "../components/ImportMobileBlocker";
import { ImportPreviewSummary } from "../components/ImportPreviewSummary";
import { ImportPreviewTable } from "../components/ImportPreviewTable";
import { ImportUploadZone } from "../components/ImportUploadZone";
import type {
  ImportConfirmInput,
  ImportConfirmRow,
  ImportFilePayload,
  ImportPreviewResult,
  LifecycleState,
} from "../types";

type Step =
  | { kind: "upload"; uploadError?: string }
  | { kind: "preview"; data: ImportPreviewResult }
  | { kind: "confirming"; data: ImportPreviewResult }
  | {
      kind: "confirmError";
      message: string;
      retryable: boolean;
      data: ImportPreviewResult;
    };

const RETRYABLE_CODES = new Set(["UNKNOWN_ERROR", "INTERNAL_ERROR"]);

function buildConfirmRows(
  rows: ImportPreviewResult["rows"],
  edited: Map<number, LifecycleState>,
): ImportConfirmRow[] {
  return rows.map((r) => ({
    rowIndex: r.rowIndex,
    tag: r.tag,
    name: r.name,
    sex: r.sex,
    birthDate: r.birthDate,
    birthDateIsEstimated: r.birthDateIsEstimated,
    acquiredAt: r.acquiredAt,
    acquisitionType: r.acquisitionType,
    initialCost: r.initialCost,
    breed: r.breed,
    motherTag: r.motherTag,
    fatherTag: r.fatherTag,
    lotName: r.lotName,
    lifecycleState: edited.get(r.rowIndex) ?? r.suggestedLifecycleState,
    lastServiceDate: r.lastServiceDate,
    isPregnant: r.isPregnant,
    parities: r.parities,
    firstCalvingAt: r.firstCalvingAt,
  }));
}

function previewErrorMessage(e: unknown): string {
  if (e instanceof ApiError && e.code === "ROW_LIMIT_EXCEEDED") {
    return "Tu archivo tiene más de 500 filas. Dividilo en partes y subí cada una por separado.";
  }
  if (e instanceof ApiError && e.code === "INVALID_FILE") {
    return "No se pudo leer el archivo. ¿Es un .xlsx válido?";
  }
  return e instanceof Error ? e.message : "Error inesperado";
}

function confirmErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    switch (e.code) {
      case "TAG_ALREADY_EXISTS":
        return "Ya existe un animal con ese tag en tu cuenta. Activá «Saltar tags que ya existen» o quitá las filas duplicadas.";
      case "DUPLICATE_TAG_IN_REQUEST":
        return "Hay tags duplicados dentro del CSV. Revisá el archivo y dejá un solo animal por tag.";
      case "INVALID_LOT_STAGE":
        return "Alguno de los animales tiene un estado terminal y no puede asignarse a un lote.";
      case "INTERNAL_ERROR":
      case "UNKNOWN_ERROR":
        return "Tuvimos un problema al guardar el import. Reintentá en unos segundos.";
    }
  }
  return "No pudimos completar el import. Reintentá en unos segundos.";
}

export function ImportHatoPage() {
  const { currentAccount, isLoading } = useCurrentAccount();

  if (isLoading || !currentAccount) {
    return <ImportPageLoading />;
  }

  return <ImportFlow accountId={currentAccount.id} />;
}

function ImportFlow({ accountId }: { accountId: string }) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>({ kind: "upload" });
  const [editedStates, setEditedStates] = useState<Map<number, LifecycleState>>(new Map());
  const [skipDuplicates, setSkipDuplicates] = useState(true);

  const previewMutation = useImportPreview(accountId);
  const confirmMutation = useImportConfirm(accountId);

  function reset() {
    setStep({ kind: "upload" });
    setEditedStates(new Map());
    previewMutation.reset();
    confirmMutation.reset();
  }

  function handleFileReady(payload: ImportFilePayload) {
    setStep({ kind: "upload" });
    previewMutation.mutate(payload, {
      onSuccess: (data) => setStep({ kind: "preview", data }),
      onError: (e) => setStep({ kind: "upload", uploadError: previewErrorMessage(e) }),
    });
  }

  function handleLifecycleChange(rowIndex: number, next: LifecycleState) {
    setEditedStates((prev) => {
      const copy = new Map(prev);
      copy.set(rowIndex, next);
      return copy;
    });
  }

  function runConfirm(input: ImportConfirmInput, previewData: ImportPreviewResult) {
    setStep({ kind: "confirming", data: previewData });
    confirmMutation.mutate(input, {
      onSuccess: (result) => {
        toast.success(
          `Se importaron ${result.created} animal${result.created === 1 ? "" : "es"} correctamente.`,
        );
        navigate("/app/hato");
      },
      onError: (e) => {
        const code = e instanceof ApiError ? e.code : "UNKNOWN_ERROR";
        setStep({
          kind: "confirmError",
          message: confirmErrorMessage(e),
          retryable: RETRYABLE_CODES.has(code),
          data: previewData,
        });
      },
    });
  }

  function handleConfirm() {
    if (step.kind !== "preview") return;
    const input: ImportConfirmInput = {
      rows: buildConfirmRows(step.data.rows, editedStates),
      skipDuplicates,
    };
    if (input.rows.length === 0) return;
    const dupesInAccount = step.data.duplicatesInAccount.length;
    const effective = skipDuplicates ? input.rows.length - dupesInAccount : input.rows.length;
    if (effective <= 0) return;
    runConfirm(input, step.data);
  }

  return (
    <>
      <ImportMobileBlocker />
      <div className="hidden md:block" data-testid="import-flow">
        <div className="mx-auto max-w-5xl px-6 py-8">
          <div className="mb-6 flex items-center gap-3">
            <Link
              to="/app/hato"
              aria-label="Volver al hato"
              className="-ml-1 inline-flex size-9 items-center justify-center rounded-2xl text-app-ink-2 transition-colors hover:bg-app-surface-2"
            >
              <ArrowLeft size={18} strokeWidth={2} />
            </Link>
            <h1 className="font-semibold text-2xl text-app-ink-1 tracking-tight">
              Importar hato desde archivo
            </h1>
          </div>

          {step.kind === "upload" ? (
            <UploadStep
              accountId={accountId}
              onFileReady={handleFileReady}
              serverError={step.uploadError}
            />
          ) : null}

          {step.kind === "preview" ? (
            <PreviewStep
              data={step.data}
              editedStates={editedStates}
              skipDuplicates={skipDuplicates}
              onSkipDuplicatesChange={setSkipDuplicates}
              onLifecycleChange={handleLifecycleChange}
              onConfirm={handleConfirm}
              onCancel={reset}
            />
          ) : null}

          {step.kind === "confirming" ? (
            <ImportConfirmResult
              kind="confirming"
              total={confirmMutation.variables?.rows.length ?? step.data.rows.length}
            />
          ) : null}

          {step.kind === "confirmError" ? (
            <ImportConfirmResult
              kind="error"
              message={step.message}
              retryable={step.retryable}
              onRetry={() => {
                if (confirmMutation.variables) {
                  runConfirm(confirmMutation.variables, step.data);
                }
              }}
              onCancel={() => setStep({ kind: "preview", data: step.data })}
            />
          ) : null}
        </div>
      </div>
    </>
  );
}

function ImportPageLoading() {
  return (
    <div className="hidden md:block" data-testid="import-flow-loading">
      <div className="mx-auto max-w-5xl px-6 py-8">
        <div className="h-8 w-64 animate-pulse rounded-2xl bg-app-surface-2" aria-hidden="true" />
        <div className="mt-6 h-48 animate-pulse rounded-2xl bg-app-surface-2" aria-hidden="true" />
        <span className="sr-only">Cargando…</span>
      </div>
    </div>
  );
}

function TemplateDownloadButton({ accountId }: { accountId: string }) {
  const [isDownloading, setIsDownloading] = useState(false);

  async function handleDownload() {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadImportTemplate(accountId);
    } catch {
      toast.error("No se pudo descargar la plantilla. Reintentá en unos segundos.");
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <div className="flex flex-col items-start gap-2 rounded-2xl border border-app-border bg-app-surface-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <p className="text-app-ink-2 text-sm leading-relaxed">
        ¿Empezás desde cero? La plantilla de Excel trae los valores permitidos como listas
        desplegables, así evitás errores de formato.
      </p>
      <button
        type="button"
        onClick={() => void handleDownload()}
        disabled={isDownloading}
        data-testid="import-template-button"
        className="inline-flex shrink-0 items-center gap-2 rounded-2xl border border-app-border bg-app-surface px-4 py-2.5 font-medium text-[14px] text-app-ink-1 transition-colors hover:bg-app-surface-2 disabled:opacity-50"
      >
        <Download size={16} strokeWidth={2} aria-hidden />
        {isDownloading ? "Descargando…" : "Descargar plantilla"}
      </button>
    </div>
  );
}

function UploadStep({
  accountId,
  onFileReady,
  serverError,
}: {
  accountId: string;
  onFileReady: (payload: ImportFilePayload) => void;
  serverError?: string;
}) {
  return (
    <div className="space-y-6">
      <p className="max-w-2xl text-app-ink-2 text-sm leading-relaxed">
        Subí un archivo CSV o Excel (.xlsx) con tu hato. Después podrás revisar la vista previa,
        ajustar el estado por animal si la clasificación automática no fue precisa, y confirmar el
        import.
      </p>
      <TemplateDownloadButton accountId={accountId} />
      {serverError ? (
        <div
          role="alert"
          data-testid="import-server-error"
          className="rounded-2xl border border-app-red bg-app-red-bg/40 px-4 py-3 text-app-red text-sm"
        >
          {serverError}
        </div>
      ) : null}
      <ImportUploadZone onFileReady={onFileReady} />
      <ImportGuideSection />
    </div>
  );
}

type PreviewStepProps = {
  data: ImportPreviewResult;
  editedStates: Map<number, LifecycleState>;
  skipDuplicates: boolean;
  onSkipDuplicatesChange: (next: boolean) => void;
  onLifecycleChange: (rowIndex: number, next: LifecycleState) => void;
  onConfirm: () => void;
  onCancel: () => void;
};

function PreviewStep({
  data,
  editedStates,
  skipDuplicates,
  onSkipDuplicatesChange,
  onLifecycleChange,
  onConfirm,
  onCancel,
}: PreviewStepProps) {
  const importable = data.rows.length;
  const duplicatesInAccount = data.duplicatesInAccount.length;
  const effectiveCount = skipDuplicates ? importable - duplicatesInAccount : importable;
  const showDuplicatesWarning = !skipDuplicates && duplicatesInAccount > 0;
  const showAllSkippedInfo = skipDuplicates && importable > 0 && effectiveCount === 0;
  const dupInFileSet = new Set(data.duplicatesInFile.map((t) => t.toLowerCase()));
  const dupInAccountSet = new Set(data.duplicatesInAccount.map((t) => t.toLowerCase()));

  return (
    <div className="space-y-5">
      <ImportPreviewSummary
        importable={importable}
        errors={data.errors.length}
        duplicates={data.duplicatesInAccount.length + data.duplicatesInFile.length}
        newLots={data.newLots}
      />

      {data.duplicatesInAccount.length > 0 ? (
        <label className="flex items-center gap-2 text-app-ink-1 text-sm">
          <input
            type="checkbox"
            checked={skipDuplicates}
            onChange={(e) => onSkipDuplicatesChange(e.target.checked)}
            className="size-4 rounded border-app-border accent-app-emerald"
          />
          Saltar tags que ya existen en mi cuenta ({data.duplicatesInAccount.length})
        </label>
      ) : null}

      <ImportPreviewTable
        rows={data.rows}
        editedStates={editedStates}
        duplicatesInFile={dupInFileSet}
        duplicatesInAccount={dupInAccountSet}
        onLifecycleChange={onLifecycleChange}
      />

      <ImportErrorList errors={data.errors} />

      {showDuplicatesWarning ? (
        <div
          role="alert"
          data-testid="import-duplicates-warning"
          className="flex items-start gap-2 rounded-2xl border border-app-amber bg-app-amber-bg/40 px-4 py-3 text-app-amber text-sm"
        >
          <AlertTriangle size={16} strokeWidth={2} className="mt-0.5 shrink-0" aria-hidden />
          <span>
            {duplicatesInAccount} fila{duplicatesInAccount === 1 ? "" : "s"} duplicada
            {duplicatesInAccount === 1 ? "" : "s"} {duplicatesInAccount === 1 ? "va" : "van"} a
            hacer que el import falle. Activá «Saltar tags que ya existen» o eliminá los duplicados
            de tu archivo antes de subir.
          </span>
        </div>
      ) : null}

      {showAllSkippedInfo ? (
        <div
          data-testid="import-all-skipped-info"
          className="flex items-start gap-2 rounded-2xl border border-app-border bg-app-surface-2 px-4 py-3 text-app-ink-2 text-sm"
        >
          <Info size={16} strokeWidth={2} className="mt-0.5 shrink-0" aria-hidden />
          <span>
            Todas las filas válidas ya existen en tu cuenta. Subí un archivo con animales nuevos
            para importar.
          </span>
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-3 border-app-border border-t pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-2xl border border-app-border bg-app-surface px-4 py-2.5 font-medium text-[14px] text-app-ink-1 transition-colors hover:bg-app-surface-2"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={effectiveCount <= 0}
          className="rounded-2xl bg-app-ink-1 px-5 py-2.5 font-medium text-[14px] text-white transition-colors hover:bg-[#1e211d] disabled:opacity-50"
          data-testid="import-confirm-button"
        >
          {effectiveCount <= 0 ? "Sin filas para importar" : `Confirmar import (${effectiveCount})`}
        </button>
      </div>
    </div>
  );
}
